import { z } from "zod";
/**
 * Schemas do domínio. Esta é a fonte da verdade dos tipos que atravessam a
 * fronteira entre o navegador e a API — invariante 1 do CLAUDE.md. Nenhum tipo
 * daqui é reescrito à mão do outro lado.
 */
/** Texto ISO 8601 em UTC, como tudo que é data no Noesis (invariante 7). */
export const zInstanteUtc = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{1,3})?Z$/, "Instante precisa ser ISO 8601 em UTC, terminado em Z");
/** Data civil `AAAA-MM-DD`, no fuso do usuário. */
export const zDiaCivil = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Dia precisa ser AAAA-MM-DD");
/** Identificador de fuso da IANA. Validado contra o próprio `Intl`. */
export const zFusoHorario = z.string().refine((v) => {
    try {
        new Intl.DateTimeFormat("en-US", { timeZone: v });
        return true;
    }
    catch {
        return false;
    }
}, { message: "Fuso horário desconhecido" });
export const zId = z.string().min(1).max(64);
export const TIPOS_ITEM = ["multipla", "certo_errado", "cartao", "cloze"];
export const zTipoItem = z.enum(TIPOS_ITEM);
/** Chave de alternativa. Cinco é o teto porque o teclado cobre 1–5. */
export const zChaveAlternativa = z.enum(["A", "B", "C", "D", "E"]);
// --- conteúdo e gabarito, discriminados por tipo (seção 4 do SPEC) ---
export const zConteudoMultipla = z.object({
    /**
     * `partialRecord`, não `record`.
     *
     * No Zod 4 um `z.record` com chave de enum é **exaustivo**: ele exige todas
     * as cinco alternativas, A a E. Uma questão de quatro alternativas — ou de
     * duas — era recusada com "expected string, received undefined" em C, D e E,
     * e a checagem de quantidade logo abaixo virava código morto, porque nada
     * fora de exatamente cinco chegava até ela.
     *
     * Ficou inerte a fase inteira porque não havia item nenhum no banco. Foi
     * encontrado na F1, etapa 2, quando o primeiro arquivo de exemplo passou pelo
     * schema. O piso e o teto de alternativas continuam sendo `conferirMultipla`.
     */
    alternativas: z.partialRecord(zChaveAlternativa, z.string().min(1)),
});
export const zGabaritoMultipla = z.object({ correta: zChaveAlternativa });
export const zConteudoCertoErrado = z.object({});
export const zGabaritoCertoErrado = z.object({ correta: z.boolean() });
export const zConteudoCartao = z.object({ verso: z.string().min(1) });
export const zGabaritoCartao = z.object({});
export const zConteudoCloze = z.object({
    lacunas: z
        .array(z.object({
        id: z.string().min(1),
        resposta: z.string().min(1),
        aceitaveis: z.array(z.string()).default([]),
    }))
        .min(1),
});
export const zGabaritoCloze = z.object({});
/**
 * O par conteúdo/gabarito de um item, discriminado pelo tipo. Validar os dois
 * juntos é o que impede uma múltipla escolha guardar gabarito de cartão.
 */
export const zCorpoItem = z.discriminatedUnion("tipo", [
    z.object({ tipo: z.literal("multipla"), conteudo: zConteudoMultipla, gabarito: zGabaritoMultipla }),
    z.object({ tipo: z.literal("certo_errado"), conteudo: zConteudoCertoErrado, gabarito: zGabaritoCertoErrado }),
    z.object({ tipo: z.literal("cartao"), conteudo: zConteudoCartao, gabarito: zGabaritoCartao }),
    z.object({ tipo: z.literal("cloze"), conteudo: zConteudoCloze, gabarito: zGabaritoCloze }),
]);
/**
 * A múltipla escolha precisa ter a alternativa correta entre as oferecidas.
 * O schema sozinho não pega isso: é uma relação entre dois campos.
 *
 * Fica numa função para o formato de importação aplicar exatamente a mesma
 * regra. Duplicar a checagem seria a segunda fonte da verdade que o invariante 1
 * proíbe — e a que fica para trás é sempre a do caminho menos usado.
 */
export function conferirMultipla(corpo, ctx, prefixo = []) {
    if (corpo.tipo !== "multipla")
        return;
    const alternativas = corpo.conteudo.alternativas ?? {};
    const correta = corpo.gabarito.correta;
    const chaves = Object.keys(alternativas);
    if (chaves.length < 2) {
        ctx.addIssue({ code: "custom", message: "Múltipla escolha precisa de ao menos duas alternativas", path: [...prefixo, "conteudo", "alternativas"] });
    }
    if (chaves.length > 5) {
        ctx.addIssue({ code: "custom", message: "Máximo de cinco alternativas: o teclado só cobre 1–5", path: [...prefixo, "conteudo", "alternativas"] });
    }
    if (correta !== undefined && !chaves.includes(correta)) {
        ctx.addIssue({ code: "custom", message: `Gabarito aponta para a alternativa ${correta}, que não existe`, path: [...prefixo, "gabarito", "correta"] });
    }
}
export const zCorpoItemCoerente = zCorpoItem.superRefine((corpo, ctx) => conferirMultipla(corpo, ctx));
// --- usuário e sessão de autenticação ---
export const zUsuarioPublico = z.object({
    id: zId,
    email: z.string().email(),
    nome: z.string().min(1),
    /** De onde sai o dia da ofensiva. Configuração, nunca constante (invariante 16). */
    fusoHorario: zFusoHorario,
    tema: z.enum(["claro", "escuro", "sistema"]),
    /**
     * A trava da primeira entrada, visível para a tela.
     *
     * Sem isto o front só descobriria a trava levando 403 numa rota qualquer, e
     * teria de adivinhar o que fazer com o erro. Com isto, `sessao.atual` já diz
     * "desenhe o formulário de senha nova".
     */
    deveRedefinirSenha: z.boolean(),
    criadoEm: zInstanteUtc,
});
export const zEntrarEntrada = z.object({
    email: z.string().email("E-mail inválido"),
    senha: z.string().min(1, "Informe a senha"),
});
/**
 * Redefinição da própria senha.
 *
 * Pede a senha atual mesmo já havendo sessão: cookie roubado não deve bastar
 * para trocar a credencial e trancar o dono para fora. O comprimento mínimo é
 * o mesmo do `semear` — 12 —, e mora aqui para o campo da tela e a validação
 * da API serem o mesmo número (invariante 4 na fronteira).
 */
export const SENHA_MINIMA = 12;
export const zRedefinirSenhaEntrada = z.object({
    senhaAtual: z.string().min(1, "Informe a senha atual"),
    senhaNova: z.string().min(SENHA_MINIMA, `A senha nova precisa de ao menos ${SENHA_MINIMA} caracteres`),
});
/**
 * Limites que a interface precisa conhecer e que **não** podem ser literais nela
 * (invariante 4). Saem da configuração do servidor e viajam com a sessão, para
 * que o `min`/`max` de um campo e a validação da API sejam sempre o mesmo número.
 */
export const zLimites = z.object({
    metaMinima: z.number().int().positive(),
    metaMaxima: z.number().int().positive(),
    itensPorSessao: z.number().int().positive(),
    /** Quantas questões o pedido a um modelo sugere de partida (Importar, ADR 041). */
    pedidoQuantidade: z.number().int().positive(),
    /** Teto do arquivo convertido em fonte, em bytes, e do texto de uma fonte (ADR 043). */
    fonteMaxArquivo: z.number().int().positive(),
    fonteMaxTexto: z.number().int().positive(),
    /**
     * Quantos minutos sem toque expiram a sessão de estudo (RN-64). A tela
     * precisa do número para dizer o que houve; escrevê-lo lá seria constante de
     * negócio no código (invariante 4).
     */
    sessaoInatividadeMinutos: z.number().int().positive(),
    /**
     * Os parâmetros do SM-2, para a tela poder mostrar a conta e não só o
     * resultado (RN-13). Definidos em `srs.ts`; o schema é declarado lá para não
     * haver duas descrições do mesmo objeto.
     */
    srs: z.object({
        fatorInicial: z.number(),
        fatorMinimo: z.number(),
        intervalo1: z.number().int(),
        intervalo2: z.number().int(),
        multiplicadorDificil: z.number(),
    }),
    /**
     * As janelas da tela de estatísticas, em dias, e qual vem escolhida. A tela
     * não inventa nenhuma: quem oferece é a configuração (invariante 4).
     */
    estatistica: z.object({
        janelas: z.array(z.number().int().positive()).min(1),
        padrao: z.number().int().positive(),
    }),
});
export const zSessaoAtual = z.object({
    usuario: zUsuarioPublico,
    /** O dia do usuário agora, já convertido pelo servidor. */
    hoje: zDiaCivil,
    limites: zLimites,
});
export const zOk = z.object({ ok: z.literal(true) });
export const zSaude = z.object({
    ok: z.boolean(),
    versao: z.string(),
    banco: z.enum(["ligado", "desligado"]),
    fusoDoUsuario: z.string().nullable(),
    hoje: zDiaCivil.nullable(),
    agoraUtc: zInstanteUtc,
});
/** Formato único de erro da API. O front nunca precisa adivinhar a forma. */
export const zErroApi = z.object({
    erro: z.object({
        codigo: z.enum([
            "nao_autenticado",
            "credenciais_invalidas",
            /**
             * Sessão válida, mas a conta está trancada até a senha ser redefinida
             * (RN da primeira entrada). Não é 401: o cookie vale, e mandar a tela
             * para o login apagaria a sessão que ela precisa usar para trocar.
             */
            "senha_a_redefinir",
            "entrada_invalida",
            "nao_encontrado",
            "conflito",
            "excesso_de_tentativas",
            /**
             * A sessão de estudo expirou por inatividade (RN-64) e a resposta chegou
             * depois disso. Não é `conflito`: a tela tem uma saída própria — montar
             * sessão nova —, e falhar em silêncio aqui perderia a resposta.
             */
            "sessao_expirada",
            /**
             * O `respondidoEm` que o cliente mandou está fora da janela que o servidor
             * aceita: velho demais para ainda creditar o dia em que foi dado, ou
             * adiantado além da folga de relógio (ADR 027). Não é `entrada_invalida`:
             * a forma está certa, e o que não bate é o relógio.
             */
            "datacao_invalida",
            /* --- inteligência artificial (RN-61 a RN-63) --- */
            /** Nem a conta nem o servidor têm chave. A tela aponta para Ajustes. */
            "ia_sem_chave",
            /** A cota diária desta conta acabou. Ela volta na virada do dia do usuário. */
            "ia_cota",
            /** O modelo não respondeu, demorou demais ou devolveu erro. */
            "ia_indisponivel",
            /** Respondeu, mas fora do que o formato aceita. */
            "ia_resposta_invalida",
            "erro_interno",
        ]),
        mensagem: z.string(),
        /** Preenchido quando o código é `entrada_invalida`. */
        campos: z.array(z.object({ caminho: z.string(), mensagem: z.string() })).optional(),
    }),
});
//# sourceMappingURL=dominio.js.map